import { makeRequest, apiClient } from '../../../../services/apiClient';
import { getOCELMetadata } from '../../../../services/api/ocel.api';

// Detect 404 or "Not Found" errors robustly across environments
const isNotFound = (err) => {
  if (!err) return false;
  const status = err?.response?.status;
  if (status === 404) return true;
  const msg = String(err?.message || err || '');
  return /Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg);
};

// Derive backend origin from axios baseURL to avoid using the frontend origin
const getBackendOrigin = () => {
  try {
    const base = apiClient?.defaults?.baseURL || '';
    if (!base) return null;
    const u = new URL(base, window?.location?.origin);
    return u.origin; // e.g., http://localhost:8000
  } catch (e) {
    return null;
  }
};

/**
 * Feature-scoped API for CountsTiles
 */
export const fetchCounts = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  return await makeRequest('/counts', { params }, 'counts (feature)');
};

export const fetchTotalEvents = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  return await makeRequest('/total-events', { params }, 'total events (feature)');
};

export const fetchTotalObjects = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  return await makeRequest('/total-objects', { params }, 'total objects (feature)');
};

export const fetchObjectTypeCount = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  try {
    return await makeRequest('/object-types/count', { params }, 'object types count (feature)');
  } catch (err) {
    // Fallback: derive count from frequencies endpoint
    try {
      const freqs = await fetchObjectTypeFrequencies(fileType);
      const mapping = freqs?.objectTypeFrequencies || freqs || {};
      const objectTypeCount = Object.keys(mapping).length;
      return { objectTypeCount };
    } catch (e2) {
      throw err; // keep original error context
    }
  }
};

export const fetchEventTypeCount = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  try {
    return await makeRequest('/event-types/count', { params }, 'event types count (feature)');
  } catch (err) {
    // Fallback: derive distinct activity types count from analytics endpoint
    try {
      const res = await makeRequest('/activity-counts', { params }, 'activity counts for event type count (feature)');
      const mapping = res?.activityCounts || res || {};
      const eventTypeCount = Object.keys(mapping).length;
      return { eventTypeCount };
    } catch (e2) {
      throw err;
    }
  }
};

export const fetchObjectTypeFrequencies = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  try {
    // Primary path
    return await makeRequest('/object-types/frequencies', { params }, 'object type frequencies (feature)');
  } catch (err) {
    // Try OCEL-prefixed path when routers are mounted under /ocel
    if (isNotFound(err)) {
      try {
        return await makeRequest('/ocel/object-types/frequencies', { params }, 'object type frequencies (feature, ocel fallback)');
      } catch (err2) {
        if (isNotFound(err2) && typeof window !== 'undefined' && window.location) {
          // Absolute URL fallbacks
          try {
            const origin = getBackendOrigin() || window.location.origin;
            const absoluteOcel = `${origin}/ocel/object-types/frequencies`;
            return await makeRequest(absoluteOcel, { params }, 'object type frequencies (feature, absolute ocel)');
          } catch (err3) {
            if (isNotFound(err3)) {
              const origin = getBackendOrigin() || window.location.origin;
              // Try absolute root without api
              const absoluteRoot = `${origin}/object-types/frequencies`;
              try {
                return await makeRequest(absoluteRoot, { params }, 'object type frequencies (feature, absolute root)');
              } catch (err4) {
                if (isNotFound(err4)) {
                  // Try absolute API-mounted paths
                  const absoluteApiOcel = `${origin}/api/ocel/object-types/frequencies`;
                  try {
                    return await makeRequest(absoluteApiOcel, { params }, 'object type frequencies (feature, absolute api/ocel)');
                  } catch (err5) {
                    if (isNotFound(err5)) {
                      const absoluteApiRoot = `${origin}/api/object-types/frequencies`;
                      try {
                        return await makeRequest(absoluteApiRoot, { params }, 'object type frequencies (feature, absolute api root)');
                      } catch (err6) {
                        if (isNotFound(err6)) {
                          const absoluteApiV1Ocel = `${origin}/api/v1/ocel/object-types/frequencies`;
                          try {
                            return await makeRequest(absoluteApiV1Ocel, { params }, 'object type frequencies (feature, absolute api/v1/ocel)');
                          } catch (err7) {
                            if (isNotFound(err7)) {
                              const absoluteApiV1Root = `${origin}/api/v1/object-types/frequencies`;
                              try {
                                return await makeRequest(absoluteApiV1Root, { params }, 'object type frequencies (feature, absolute api/v1 root)');
                              } catch (err8) {
                                if (isNotFound(err8)) {
                                  // Additional variants used in some deployments
                                  const absoluteV1Ocel = `${origin}/v1/ocel/object-types/frequencies`;
                                  try {
                                    return await makeRequest(absoluteV1Ocel, { params }, 'object type frequencies (feature, absolute v1/ocel)');
                                  } catch (err9) {
                                    if (isNotFound(err9)) {
                                      const absoluteV1Root = `${origin}/v1/object-types/frequencies`;
                                      try {
                                        return await makeRequest(absoluteV1Root, { params }, 'object type frequencies (feature, absolute v1 root)');
                                      } catch (err10) {
                                        if (isNotFound(err10)) {
                                          // Gateway-prefixed variants
                                          const candidates = [
                                            `${origin}/gateway/api/ocel/object-types/frequencies`,
                                            `${origin}/gateway/api/v1/ocel/object-types/frequencies`,
                                            `${origin}/gateway/v1/ocel/object-types/frequencies`,
                                            `${origin}/gateway/ocel/object-types/frequencies`,
                                            `${origin}/gateway/api/object-types/frequencies`,
                                            `${origin}/gateway/api/v1/object-types/frequencies`,
                                            `${origin}/gateway/v1/object-types/frequencies`,
                                            `${origin}/gateway/object-types/frequencies`,
                                          ];
                                          for (const url of candidates) {
                                            try {
                                              return await makeRequest(url, { params }, `object type frequencies (feature, ${url})`);
                                            } catch (e) {
                                              if (!isNotFound(e)) throw e;
                                            }
                                          }
                                        }
                                        throw err10;
                                      }
                                    }
                                    throw err9;
                                  }
                                }
                                throw err8;
                              }
                            }
                            throw err7;
                          }
                        }
                        throw err6;
                      }
                    }
                    throw err5;
                  }
                }
                throw err4;
              }
            }
            throw err3;
          }
        }
        throw err2;
      }
    }
    throw err;
  }
};

export const fetchEventTypesPerObjectType = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  // Fallback to existing endpoint that returns activities per object type and derive counts client-side
  const res = await makeRequest('/object-type-activities', { params }, 'event types per object type (feature)');
  const activitiesMap = res?.objectTypeActivities || {};
  const counts = Object.fromEntries(
    Object.entries(activitiesMap).map(([type, acts]) => [type, Array.isArray(acts) ? acts.length : 0])
  );
  return { eventTypesPerObjectType: counts };
};

export const fetchEventCountsPerObjectType = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  try {
    // Primary path
    return await makeRequest('/events/per-object-type', { params }, 'event counts per object type (feature)');
  } catch (err) {
    // Try OCEL-prefixed path
    if (isNotFound(err)) {
      try {
        return await makeRequest('/ocel/events/per-object-type', { params }, 'event counts per object type (feature, ocel fallback)');
      } catch (err2) {
        if (isNotFound(err2) && typeof window !== 'undefined' && window.location) {
          try {
            const origin = getBackendOrigin() || window.location.origin;
            const absoluteOcel = `${origin}/ocel/events/per-object-type`;
            return await makeRequest(absoluteOcel, { params }, 'event counts per object type (feature, absolute ocel)');
          } catch (err3) {
            if (isNotFound(err3)) {
              const origin = getBackendOrigin() || window.location.origin;
              // Try absolute root without api
              const absoluteRoot = `${origin}/events/per-object-type`;
              try {
                return await makeRequest(absoluteRoot, { params }, 'event counts per object type (feature, absolute root)');
              } catch (err4) {
                if (isNotFound(err4)) {
                  // Try absolute API-mounted paths
                  const absoluteApiOcel = `${origin}/api/ocel/events/per-object-type`;
                  try {
                    return await makeRequest(absoluteApiOcel, { params }, 'event counts per object type (feature, absolute api/ocel)');
                  } catch (err5) {
                    if (isNotFound(err5)) {
                      const absoluteApiRoot = `${origin}/api/events/per-object-type`;
                      try {
                        return await makeRequest(absoluteApiRoot, { params }, 'event counts per object type (feature, absolute api root)');
                      } catch (err6) {
                        if (isNotFound(err6)) {
                          const absoluteApiV1Ocel = `${origin}/api/v1/ocel/events/per-object-type`;
                          try {
                            return await makeRequest(absoluteApiV1Ocel, { params }, 'event counts per object type (feature, absolute api/v1/ocel)');
                          } catch (err7) {
                            if (isNotFound(err7)) {
                              const absoluteApiV1Root = `${origin}/api/v1/events/per-object-type`;
                              try {
                                return await makeRequest(absoluteApiV1Root, { params }, 'event counts per object type (feature, absolute api/v1 root)');
                              } catch (err8) {
                                if (isNotFound(err8)) {
                                  // Additional variants used in some deployments
                                  const absoluteV1Ocel = `${origin}/v1/ocel/events/per-object-type`;
                                  try {
                                    return await makeRequest(absoluteV1Ocel, { params }, 'event counts per object type (feature, absolute v1/ocel)');
                                  } catch (err9) {
                                    if (isNotFound(err9)) {
                                      const absoluteV1Root = `${origin}/v1/events/per-object-type`;
                                      return await makeRequest(absoluteV1Root, { params }, 'event counts per object type (feature, absolute v1 root)');
                                    }
                                    throw err9;
                                  }
                                }
                                throw err8;
                              }
                            }
                            throw err7;
                          }
                        }
                        throw err6;
                      }
                    }
                    throw err5;
                  }
                }
                throw err4;
              }
            }
            throw err3;
          }
        }
        throw err2;
      }
    }
    throw err;
  }
};

export const fetchTimeSpanKPI = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  try {
    // KPI router is mounted at /api/kpi
    return await makeRequest('/kpi/time-span', { params }, 'time span KPI (feature)');
  } catch (error) {
    // Fallback: derive time span from OCEL metadata if dedicated endpoint is not available
    try {
      const meta = fileType
        ? await getOCELMetadata(fileType)
        : await makeRequest('/ocel/metadata', { params }, 'ocel metadata for time span fallback (feature)');
      const startIso = meta?.date_range?.start;
      const endIso = meta?.date_range?.end;
      if (!startIso || !endIso) {
        throw new Error('Missing date_range in metadata');
      }
      const startDate = new Date(startIso);
      const endDate = new Date(endIso);
      const durationSeconds = Math.max(0, Math.floor((endDate - startDate) / 1000));
      const days = Math.round((durationSeconds / 86400) * 100) / 100;
      const weeks = Math.round((days / 7) * 100) / 100;
      const months = Math.round((days / 30.44) * 100) / 100;
      const pretty = `${startIso.split('T')[0]} → ${endIso.split('T')[0]} (${days.toFixed(2)} days)`;
      return {
        file_type: meta?.file_type || (fileType || 'main'),
        start_timestamp: startIso,
        end_timestamp: endIso,
        duration_seconds: durationSeconds,
        duration_days: days,
        duration_weeks: weeks,
        duration_months_approx: months,
        pretty,
      };
    } catch (fallbackError) {
      // If fallback also fails, rethrow the original error to keep behavior predictable
      throw error;
    }
  }
};

